import { useEffect, useRef, type ReactNode } from 'react';
import { Icon, type UiIcon } from './Icon';
import styles from './ui.module.css';

export const Sheet = ({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    ref.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      prev?.focus?.();
    };
  }, [onClose]);

  return (
    <>
      <div className={styles.backdrop} onClick={onClose} />
      <div ref={ref} tabIndex={-1} role="dialog" aria-modal="true" aria-label={title} className={`${styles.sheet} surface`} data-no-tap>
        <div className={styles.grab} />
        <div className={styles.sheetHeader}>
          <h2 className={styles.sheetTitle}>{title}</h2>
          <button className={styles.iconButton} onClick={onClose} aria-label="Close">
            <Icon name="close" />
          </button>
        </div>
        {children}
      </div>
    </>
  );
};

export const Switch = ({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) => (
  <div className={styles.switchRow}>
    <span>{label}</span>
    <button role="switch" aria-checked={checked} aria-label={label} className={styles.switch} onClick={() => onChange(!checked)} />
  </div>
);

export const Chips = <T extends string | number>({
  options,
  value,
  onChange,
  label,
}: {
  options: { value: T; label: string }[];
  value: T | null;
  onChange: (v: T) => void;
  label: string;
}) => (
  <div className={styles.chips} role="group" aria-label={label}>
    {options.map(o => (
      <button key={String(o.value)} className={styles.chip} aria-pressed={o.value === value} onClick={() => onChange(o.value)}>
        {o.label}
      </button>
    ))}
  </div>
);

export const Stepper = ({
  value,
  display,
  onMinus,
  onPlus,
  label,
}: {
  value: number;
  display: string;
  onMinus: () => void;
  onPlus: () => void;
  label: string;
}) => (
  <div className={styles.stepper} role="group" aria-label={label}>
    <button className={styles.stepBtn} onClick={onMinus} aria-label={`Decrease ${label}`}>
      −
    </button>
    <span className={styles.stepValue} aria-live="polite" data-value={value}>
      {display}
    </span>
    <button className={styles.stepBtn} onClick={onPlus} aria-label={`Increase ${label}`}>
      +
    </button>
  </div>
);

export type TabId = 'today' | 'explore' | 'profile';

const tabs: { id: TabId; label: string; icon: UiIcon }[] = [
  { id: 'today', label: 'Today', icon: 'today' },
  { id: 'explore', label: 'Explore', icon: 'explore' },
  { id: 'profile', label: 'Profile', icon: 'profile' },
];

export const TabBar = ({ active, onSelect }: { active: TabId; onSelect: (t: TabId) => void }) => (
  <nav className={`${styles.tabs} surface`} aria-label="Main">
    {tabs.map(t => (
      <button key={t.id} className={styles.tab} aria-current={t.id === active ? 'page' : undefined} onClick={() => onSelect(t.id)}>
        <Icon name={t.icon} size={22} />
        {t.label}
      </button>
    ))}
  </nav>
);
